/**
 * PersonaModal — pick your role from the canonical 6 personas (lib/personas.ts).
 * Used both by the first-run PersonaPrompt and the profile-menu "Your role"
 * entry. Saving sets User.persona (and the server derives the legacy
 * preferredView from its lens), which tailors /today + the daily briefing.
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { PERSONAS_BY_LENS, LENS_LABEL, getPersona, type PersonaLens } from '../lib/personas';
import { authApi } from '../lib/api';
import { useAuthStore } from '../store/useAuthStore';

export default function PersonaModal({ onClose, dismissible = true }: { onClose: () => void; dismissible?: boolean }) {
  const { user, updateUser } = useAuthStore();
  const [saving, setSaving] = useState<string | null>(null);
  const current = user?.persona ?? null;

  const pick = async (key: string) => {
    setSaving(key);
    try {
      const r = await authApi.setPersona(key);
      updateUser({ persona: r.data.persona, preferredView: r.data.preferredView });
      toast.success(`You're set as ${getPersona(key)?.label}`);
      onClose();
    } catch {
      toast.error('Could not save your role');
      setSaving(null);
    }
  };

  // Clearing the role is the off-switch for the role-based briefing emails.
  const clear = async () => {
    setSaving('__clear__');
    try {
      await authApi.setPersona(null);
      updateUser({ persona: null });
      toast.success('Role cleared — role-based emails turned off');
      onClose();
    } catch {
      toast.error('Could not clear your role');
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[55] flex items-center justify-center p-4" onClick={() => dismissible && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="brand-header-gradient-accent text-white px-6 py-5">
          <h2 className="text-xl font-extrabold">What’s your role?</h2>
          <p className="text-teal-200 text-sm mt-1">We’ll tailor your <strong>daily briefing</strong> to what you care about — and turn on the role-based <strong>email briefings</strong> that fit. You can remove your role any time to stop them.</p>
        </div>
        <div className="px-6 py-5 max-h-[58vh] overflow-auto space-y-4">
          {(['team', 'leaders'] as PersonaLens[]).map(lens => (
            <div key={lens}>
              <div className="text-[11px] font-bold tracking-normal text-ink-faint mb-2">{LENS_LABEL[lens]}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {PERSONAS_BY_LENS[lens].map(p => (
                  <button
                    key={p.key} onClick={() => pick(p.key)} disabled={!!saving}
                    className={`text-left rounded-xl border p-3 transition-colors ${current === p.key ? 'border-brand-primary dark:border-brand-accent bg-brand-primary-light dark:bg-brand-primary/25' : 'border-line hover:border-brand-primary hover:bg-wash'} ${saving === p.key ? 'opacity-60' : ''}`}
                  >
                    <div className="font-semibold text-ink text-sm flex items-center gap-1.5">
                      {p.label}{current === p.key && <span className="text-brand-primary dark:text-brand-accent text-[11px]">✓</span>}
                    </div>
                    <div className="text-[11px] text-ink-faint mt-0.5 leading-snug">{p.blurb}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {(dismissible || current) && (
          <div className="px-6 py-3 bg-wash border-t border-line flex items-center justify-between gap-3">
            {current ? (
              <button onClick={clear} disabled={!!saving}
                className="text-sm font-medium text-ink-faint hover:text-brand-danger px-3 py-1.5 rounded-lg disabled:opacity-50">
                {saving === '__clear__' ? 'Removing…' : 'Remove my role (stops role-based emails)'}
              </button>
            ) : <span />}
            {dismissible && <button onClick={onClose} className="text-sm font-medium text-ink-faint hover:text-ink-muted px-3 py-1.5 rounded-lg">Maybe later</button>}
          </div>
        )}
      </div>
    </div>
  );
}
